import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDistanceToNow } from 'date-fns'
import { useNotes, useCreateNote, useUpdateNote, useDeleteNote, type Note } from './useNotes'
import { DeleteConfirmModal } from '../contacts/DeleteConfirmModal'
import { activeDateFnsLocale } from '../../lib/locale'

type Props = {
  originType: 'Hotspot' | 'Contact'
  originId: number
}

export function NotesList({ originType, originId }: Props) {
  const { t } = useTranslation('notes')
  const { data: notes, isLoading } = useNotes({ originType, originId })
  const createNote = useCreateNote()
  const updateNote = useUpdateNote()
  const deleteNote = useDeleteNote()

  const [newTitle, setNewTitle] = useState('')
  const [newText, setNewText] = useState('')

  const [editingId, setEditingId] = useState<number | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [editText, setEditText] = useState('')

  const [deletingId, setDeletingId] = useState<number | null>(null)

  function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!newText.trim()) return
    createNote.mutate(
      { origin_type: originType, origin_id: originId, title: newTitle, text: newText },
      {
        onSuccess: () => {
          setNewTitle('')
          setNewText('')
        },
      }
    )
  }

  function handleUpdate(e: React.FormEvent, id: number) {
    e.preventDefault()
    if (!editText.trim()) return
    updateNote.mutate(
      { id, origin_type: originType, origin_id: originId, title: editTitle, text: editText },
      {
        onSuccess: () => {
          setEditingId(null)
          setEditTitle('')
          setEditText('')
        },
      }
    )
  }

  function startEdit(note: Note) {
    setEditingId(note.id ?? null)
    setEditTitle(note.title ?? '')
    setEditText(note.text ?? '')
  }

  function cancelEdit() {
    setEditingId(null)
    setEditTitle('')
    setEditText('')
  }

  return (
    <div className="notes-list" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Inline Add Form */}
      <form onSubmit={handleCreate} className="card card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h3 className="section-heading">{t('addNote.heading')}</h3>
        <input
          type="text"
          className="form-control"
          placeholder={t('addNote.placeholderTitle')}
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          disabled={createNote.isPending}
        />
        <textarea
          className="form-control"
          placeholder={t('addNote.placeholderText')}
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          disabled={createNote.isPending}
          required
          rows={3}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="submit" className="btn btn-primary" disabled={createNote.isPending || !newText.trim()}>
            {createNote.isPending ? t('addNote.submitting') : t('addNote.submit')}
          </button>
        </div>
      </form>

      {/* List of Notes */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {isLoading && <p>{t('list.loading')}</p>}
        {!isLoading && notes && notes.length === 0 && (
          <p className="muted">{t('list.empty')}</p>
        )}
        {!isLoading && notes && notes.map((note) => (
          <div key={note.id} className="card card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {editingId === note.id ? (
              <form onSubmit={(e) => handleUpdate(e, note.id!)} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('edit.placeholderTitle')}
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  disabled={updateNote.isPending}
                />
                <textarea
                  className="form-control"
                  placeholder={t('edit.placeholderText')}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  disabled={updateNote.isPending}
                  required
                  rows={3}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button type="button" className="btn btn-ghost" onClick={cancelEdit} disabled={updateNote.isPending}>
                    {t('common:actions.cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={updateNote.isPending || !editText.trim()}>
                    {updateNote.isPending ? t('edit.saving') : t('common:actions.save')}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    {note.title && <h4 style={{ margin: 0 }}>{note.title}</h4>}
                    {note.updated_at && (
                      <small className="muted">
                        {formatDistanceToNow(new Date(note.updated_at), { addSuffix: true, locale: activeDateFnsLocale() })}
                      </small>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => startEdit(note)}>
                      {t('common:actions.edit')}
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => setDeletingId(note.id ?? null)}>
                      {t('common:actions.delete')}
                    </button>
                  </div>
                </div>
                <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{note.text}</p>
              </>
            )}
          </div>
        ))}
      </div>

      <DeleteConfirmModal
        title={t('delete.modalTitle')}
        message={t('delete.modalMessage')}
        isOpen={deletingId !== null}
        isPending={deleteNote.isPending}
        onConfirm={() => {
          if (deletingId !== null) {
            deleteNote.mutate(
              { id: deletingId, origin_type: originType, origin_id: originId },
              {
                onSuccess: () => setDeletingId(null),
              }
            )
          }
        }}
        onClose={() => setDeletingId(null)}
      />
    </div>
  )
}
